/* eslint-disable prettier/prettier */
import { addGlobalMessage, handleWithErrorMessage, handleWithGlobalMessage } from '@/services/utils';
import { pick, get } from 'lodash';
import api from '@/services/modules/bookings/bookings';
import {
  SET_BOOKINGS,
  SET_CURRENT_BOOKING,
  SET_CURRENT_BOOKING_CANDIDATES,
  SET_CANDIDATES_FOR_SUBMIT,
  SET_CURRENT_BOOKING_COMMENTS,
  SET_CURRENT_BOOKING_PUBLISHING_SCHEDULE,
  SET_IS_FETCHING_BOOKINGS,
  SET_IS_FETCHING_CURRENT_BOOKING,
  SET_IS_FETCHING_CURRENT_BOOKING_CANDIDATES,
  SET_IS_FETCHING_CANDIDATES_FOR_SUBMIT,
  SET_IS_FETCHING_CURRENT_BOOKING_COMMENTS,
  SET_IS_FETCHING_CURRENT_BOOKING_PUBLISHING_SCHEDULE,
  SET_EVALUATION_STATUSES,
  SET_BOOKING_OPTIONS,
  SET_IS_FETCHING_BOOKING_OPTIONS,
  SET_USERS_FOR_SHARING,
  SET_IS_FETCHING_USERS_FOR_SHARING,
  SET_PUBLISHING_CHANNELS,
  SET_IS_FETCHING_PUBLISHING_CHANNELS
} from '@/store/modules/bookings/mutations';
import { GET_BOOKINGS, GET_CURRENT_BOOKING } from '@/store/modules/bookings/getters';
import {
  include,
  USER
} from "@/models/relationships";

export const SUBMIT_BOOKING_FOR_APPROVAL = "SUBMIT_BOOKING_FOR_APPROVAL";
export const DELETE_BOOKING = "DELETE_BOOKING";
export const APPROVE_BOOKING = "APPROVE_BOOKING";
export const ACCEPT_BOOKING = "ACCEPT_BOOKING";
export const TRIGGER_PUBLISHING_SCHEDULE_ACTION = "TRIGGER_PUBLISHING_SCHEDULE_ACTION";
export const EDIT_BOOKING = "EDIT_BOOKING";
export const EDIT_BOOKING_WITH_MESSAGE = "EDIT_BOOKING_WITH_MESSAGE";
export const CANCEL_BOOKING = "CANCEL_BOOKING";
export const REJECT_BOOKING = "REJECT_BOOKING";
export const FETCH_CURRENT_BOOKING = "FETCH_CURRENT_BOOKING";
export const FETCH_CURRENT_BOOKING_COMMENTS = "FETCH_CURRENT_BOOKING_COMMENTS";
export const FETCH_CURRENT_BOOKING_CANDIDATES = "FETCH_CURRENT_BOOKING_CANDIDATES";
export const FETCH_BOOKING_CANDIDATES_FOR_SUBMIT = "FETCH_BOOKING_CANDIDATES_FOR_SUBMIT";
export const FETCH_CURRENT_BOOKING_PUBLISHING_SCHEDULE = "FETCH_CURRENT_BOOKING_PUBLISHING_SCHEDULE";
export const UPDATE_PUBLISHING_SCHEDULE_COST = "UPDATE_PUBLISHING_SCHEDULE_COST";
export const FETCH_BOOKINGS = "FETCH_BOOKINGS";
export const FETCH_BASIC_BOOKINGS = "FETCH_BASIC_BOOKINGS";
export const CREATE_BOOKING = "CREATE_BOOKING";
export const ADD_COMMENT = "ADD_COMMENT";
export const FETCH_EVALUATION_STATUSES = "FETCH_EVALUATION_STATUSES";
export const CLOSE_BOOKING = "CLOSE_BOOKING";
export const ADD_BOOKING_FILE = "ADD_BOOKING_FILE";
export const FETCH_USERS_FOR_SHARING = "FETCH_USERS_FOR_SHARING";
export const SHARE_BOOKING = "SHARE_BOOKING";
export const FETCH_BOOKING_OPTIONS = "FETCH_BOOKING_OPTIONS";
export const EDIT_BOOKING_OPTIONS = "EDIT_BOOKING_OPTIONS";
export const FETCH_PUBLISHING_CHANNELS = "FETCH_PUBLISHING_CHANNELS";
export const CREATE_PUBLISHING_SCHEDULE = "CREATE_PUBLISHING_SCHEDULE";
export const REOPEN_BOOKING = "REOPEN_BOOKING";
export const UPDATE_BOOKING_APPROVERS = "UPDATE_BOOKING_APPROVERS";

export const BOOKINGS_NAMESPACE = "bookings";

const bookingsActions = {
  async [SUBMIT_BOOKING_FOR_APPROVAL]({ dispatch, commit }, bookingId) {
    const { data: booking } = await handleWithGlobalMessage(
      () => api({ commit }).submitForApproval(bookingId),
      dispatch
    );
    commit(SET_CURRENT_BOOKING, booking);
  },
  async [DELETE_BOOKING]({ dispatch, commit, getters }, bookingId) {
    await handleWithGlobalMessage(
      () => api({ commit }).deleteBooking(bookingId),
      dispatch
    );
    const currentBookings = getters[GET_BOOKINGS];
    const newBookings = currentBookings.filter(
      booking => booking.id !== bookingId
    );
    commit(SET_CURRENT_BOOKING, null);
    commit(SET_BOOKINGS, newBookings);
  },
  async [APPROVE_BOOKING]({ dispatch, commit }, bookingId) {
    const { data: booking } = await handleWithGlobalMessage(
      () => api({ commit }).approveBooking(bookingId),
      dispatch
    );
    commit(SET_CURRENT_BOOKING, booking);
  },
  async [ACCEPT_BOOKING]({ dispatch, commit }, bookingId) {
    const { data: booking } = await handleWithGlobalMessage(
      () => api({ commit }).acceptBooking(bookingId),
      dispatch
    );
    commit(SET_CURRENT_BOOKING, booking);
  },
  async [EDIT_BOOKING](
    { dispatch, commit },
    { bookingId, editedBooking }
  ) {
    const { data: booking } = await handleWithGlobalMessage(
      () => api({ commit }).editBooking(bookingId, editedBooking),
      dispatch
    );
    commit(SET_CURRENT_BOOKING, booking);
  },
  async [EDIT_BOOKING_WITH_MESSAGE](
    { dispatch, commit, getters },
    { bookingId, editedBooking }
  ) {
    const { data: booking } = await handleWithGlobalMessage(
      () => api({ commit }).editBooking(bookingId, editedBooking),
      dispatch
    );
    const newBooking = {
      ...getters[GET_CURRENT_BOOKING],
      ...pick(booking, Object.keys(editedBooking))
    };
    commit(SET_CURRENT_BOOKING, newBooking);
  },
  async [TRIGGER_PUBLISHING_SCHEDULE_ACTION]({ dispatch, commit }, { bookingId, unitId, action }) {
    await handleWithGlobalMessage(
      () => api({ commit }).triggerPublishingScheduleAction(bookingId, unitId, action),
      dispatch
    );
  },
  async [CANCEL_BOOKING]({ dispatch, commit }, bookingId) {
    const { data: booking } = await handleWithGlobalMessage(
      () => api({ commit }).cancelBooking(bookingId),
      dispatch
    );
    commit(SET_CURRENT_BOOKING, booking);
  },
  async [REJECT_BOOKING]({ dispatch, commit }, bookingId) {
    const { data: booking } = await handleWithGlobalMessage(
      () => api({ commit }).rejectBooking(bookingId),
      dispatch
    );
    commit(SET_CURRENT_BOOKING, booking);
  },
  async [CLOSE_BOOKING]({ dispatch, commit }, bookingId) {
    const { data: booking } = await handleWithGlobalMessage(
      () => api({ commit }).closeBooking(bookingId),
      dispatch
    );
    commit(SET_CURRENT_BOOKING, booking);
  },
  async [REOPEN_BOOKING]({ dispatch, commit }, bookingId) {
    const { data: booking } = await handleWithGlobalMessage(
      () => api({ commit }).reopenBooking(bookingId),
      dispatch
    );
    commit(SET_CURRENT_BOOKING, booking);
  },
  async [CREATE_BOOKING]({ dispatch, commit }, templateId) {
    try {
      return api({ commit }).createBooking(templateId);
    } catch (e) {
      addGlobalMessage(dispatch, {
        type: "error",
        meta: { title: e.message }
      });
    }
  },
  async [FETCH_CURRENT_BOOKING]({ dispatch, commit, state }, bookingId) {
    if (!bookingId) {
      bookingId = state.currentBooking.id;
    }
    return handleWithErrorMessage({
      dispatch,
      request: () => api({ commit }).getBooking(bookingId),
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_CURRENT_BOOKING, isLoading),
      setDataFunction: ({ data }) => commit(SET_CURRENT_BOOKING, data)
    });
  },
  async [FETCH_CURRENT_BOOKING_COMMENTS]({ dispatch, commit }, bookingId) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api({ commit }).getBookingComments(bookingId),
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_CURRENT_BOOKING_COMMENTS, isLoading),
      setDataFunction: ({ data }) => commit(SET_CURRENT_BOOKING_COMMENTS, data)
    });
  },
  async [ADD_COMMENT](
    { dispatch, commit },
    { bookingId, comment }
  ) {
      return handleWithErrorMessage({
        dispatch,
        request: () => api({ commit }).addBookingComment(bookingId, comment),
        setLoadingFunction: isLoading =>
          commit(SET_IS_FETCHING_CURRENT_BOOKING_COMMENTS, isLoading)
      });
  },
  async [FETCH_CURRENT_BOOKING_CANDIDATES](
    { dispatch, commit, state },
    { bookingId, params }
  ) {
    const id = bookingId || get(state.currentBooking, 'id')
    if(id) {
      return handleWithErrorMessage({
        dispatch,
        request: () => api({ commit }).getBookingCandidates({
          bookingId: id,
          includes: include(USER),
          params
        }),
        setLoadingFunction: isLoading =>
          commit(SET_IS_FETCHING_CURRENT_BOOKING_CANDIDATES, isLoading),
        setDataFunction: ({ data }) =>
          commit(SET_CURRENT_BOOKING_CANDIDATES, data)
      });
    }
  },
  async [FETCH_BOOKING_CANDIDATES_FOR_SUBMIT](
    { dispatch, commit },
    { bookingId, params }
  ) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api({ commit }).getBookingCandidates({ bookingId, params, includes: include(USER) }),
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_CANDIDATES_FOR_SUBMIT, isLoading),
      setDataFunction: ({ data }) =>
        commit(SET_CANDIDATES_FOR_SUBMIT, data)
    });
  },
  async [FETCH_CURRENT_BOOKING_PUBLISHING_SCHEDULE](
    { dispatch, commit },
    bookingId
  ) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api({ commit }).getBookingPublishingSchedule(bookingId),
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_CURRENT_BOOKING_PUBLISHING_SCHEDULE, isLoading),
      setDataFunction: ({ data }) =>
        commit(SET_CURRENT_BOOKING_PUBLISHING_SCHEDULE, data)
    });
  },
  async [UPDATE_PUBLISHING_SCHEDULE_COST](
    { dispatch, commit },
    { id, data, bookingId }
  ) {
    await handleWithGlobalMessage(
      () => api({ commit }).updatePublishingScheduleCost({ id, data, bookingId }),
      dispatch
    );
  },
  async [FETCH_BOOKINGS]({ dispatch, commit }, params) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api({ commit }).getBookings(params),
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_BOOKINGS, isLoading),
      setDataFunction: ({ data }) => commit(SET_BOOKINGS, data)
    });
  },
  async [FETCH_BASIC_BOOKINGS]({ dispatch, commit }) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api({ commit }).getBasicBookings(),
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_BOOKINGS, isLoading),
      setDataFunction: ({ data }) => commit(SET_BOOKINGS, data)
    });
  },
  async [FETCH_EVALUATION_STATUSES]({ dispatch, commit }, bookingId) {
    return handleWithErrorMessage({
      dispatch,
      request: () =>
        api({ commit }).getBookingEvaluationStatuses(bookingId),
      setDataFunction: ({ data }) => commit(SET_EVALUATION_STATUSES, data)
    });
  },
  async [ADD_BOOKING_FILE]({ dispatch, commit }, { id, data }) {
    return handleWithErrorMessage({
      request: () => api({ commit }).createBookingFile(id, data),
      dispatch
    });
  },
  async [FETCH_USERS_FOR_SHARING]({ dispatch, commit }, bookingId) {
    return handleWithErrorMessage({
      dispatch,
      request: () =>
        api({ commit }).getUsersForSharing(bookingId),
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_USERS_FOR_SHARING, isLoading),
      setDataFunction: ({ data }) => commit(SET_USERS_FOR_SHARING, data)
    });
  },
  async [SHARE_BOOKING]({ dispatch, commit }, { bookingId, data }) {
    return handleWithErrorMessage({
      request: () => api({ commit }).shareBooking(bookingId, data),
      dispatch
    });
  },
  async [FETCH_BOOKING_OPTIONS]({ dispatch, commit }, id) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api({ commit }).getBookingOptions(id),
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_BOOKING_OPTIONS, isLoading),
      setDataFunction: ({ data }) => commit(SET_BOOKING_OPTIONS, data)
    });
  },
  async [EDIT_BOOKING_OPTIONS]({ dispatch, commit }, { bookingId, data }) {
   await handleWithGlobalMessage(
      () => api({ commit }).updateBookingOptions({ bookingId, data }),
      dispatch
    );
    dispatch(FETCH_BOOKING_OPTIONS, bookingId);
  },
  async [FETCH_PUBLISHING_CHANNELS]({ dispatch, commit }, params) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api({ commit }).getPublishingChannels(params),
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_PUBLISHING_CHANNELS, isLoading),
      setDataFunction: ({ data }) => commit(SET_PUBLISHING_CHANNELS, data)
    });
  },
  async [CREATE_PUBLISHING_SCHEDULE]({ dispatch, commit }, { bookingId, channel_id }) {
    try {
      return api({ commit }).addPublishingChannel({ bookingId, channel_id });
    } catch (e) {
      addGlobalMessage(dispatch, {
        type: "error",
        meta: { title: e.message }
      });
    }
  },
  async [UPDATE_BOOKING_APPROVERS]({ dispatch, commit }, { id, approvers }) {
    await handleWithErrorMessage({
      request: () => api({ commit }).updateBookingApprovers({ id, approvers }),
      dispatch
    });
    dispatch(FETCH_CURRENT_BOOKING, id);
  }
};

export default bookingsActions;
